import { FC, useState } from "react";
import { createPortal } from "react-dom";
import { useData } from "data/data-provider";
import { formatPrice } from "utils/convertNumbThousand";
import { ArrowUpRightIcon } from "@heroicons/react/24/outline";
import bonzahLogo from "images/bonzah.png";

export interface FleetCardProps {
  className?: string;
  data?: any;
  size?: "default" | "small";
  simpleImg?: boolean;
  onClick?: any;
  clickFunction?: any;
  loading?: boolean;
}

// Premium fleet card for Design B — hover-lift, image kenburns, gold accent
// for price/CTA, navy stat row. Falls back gracefully when fleet_photos is
// empty.
const FleetCard: FC<FleetCardProps> = ({
  className = "",
  data,
  onClick,
  clickFunction,
}) => {
  const { ourCompany } = useData();
  const [isHovered, setIsHovered] = useState(false);
  const [tooltipPosition, setTooltipPosition] = useState<{
    top: number;
    left: number;
  }>({ top: 0, left: 0 });
  const [loadingId, setLoadingId] = useState<string | null>(null);
  const {
    id,
    fleet_photos,
    name,
    color,
    description,
    seats,
    avg_car_price_per_day,
    thumbnail_photo_url,
    doors,
    increased_price,
    fleet_class,
    transmission,
    has_bonzah_insurance,
  } = data!;

  // Bonzah "available" badge — parity with Design A's FleetCard. Shows only
  // when the company enabled the tag AND this fleet carries Bonzah insurance.
  const shouldShowBonzahBadge =
    ourCompany?.show_bonzah_tag === true && has_bonzah_insurance;

  const increasedPriceNum = Number(increased_price);
  const currentPriceNum = Number(avg_car_price_per_day ?? 0);
  const hasDiscount =
    Number.isFinite(increasedPriceNum) &&
    increasedPriceNum > 0 &&
    increasedPriceNum > currentPriceNum &&
    currentPriceNum > 0;
  const discountPercent = hasDiscount
    ? Math.round(
        ((increasedPriceNum - currentPriceNum) / increasedPriceNum) * 100,
      )
    : 0;

  const className_imgSrc =
    fleet_photos && fleet_photos.length > 0 && thumbnail_photo_url?.includes("http")
      ? thumbnail_photo_url
      : fleet_photos && fleet_photos.length > 0
        ? `https://fleet-management-images-upload-be.s3.amazonaws.com/${encodeURIComponent(thumbnail_photo_url)}`
        : "https://tenant.fleetwire.io/images/vehicle-placeholder.jpg";

  const handleMouseEnterDesc = (e: React.MouseEvent) => {
    setIsHovered(true);
    const rect = e.currentTarget.getBoundingClientRect();
    setTooltipPosition({
      top: rect.top + window.scrollY,
      left: rect.left + window.scrollX,
    });
  };
  const handleMouseLeaveDesc = () => setIsHovered(false);

  const handleCtaClick = async (e: React.MouseEvent) => {
    e.stopPropagation();
    if (!clickFunction) {
      onClick?.();
      return;
    }
    setLoadingId(id);
    try {
      await clickFunction(id);
    } finally {
      setLoadingId(null);
    }
  };

  return (
    <article
      onClick={onClick}
      className={`group relative flex flex-col overflow-hidden rounded-3xl bg-card-color border border-text-color/10
        transform-gpu transition-all duration-500 ease-[cubic-bezier(0.16,1,0.3,1)] cursor-pointer
        hover:-translate-y-1.5 hover:shadow-2xl hover:shadow-text-color/15 hover:border-primary-200/40
        ${className}`}
      data-nc-id="StayCard"
    >
      {/* Image with kenburns */}
      <div className="relative overflow-hidden">
        <img
          src={className_imgSrc}
          alt={name}
          className="h-60 sm:h-64 w-full object-cover transition-transform duration-[1100ms] ease-out group-hover:scale-[1.08]"
        />
        {/* Soft gradient at bottom for badge legibility */}
        <div className="pointer-events-none absolute inset-x-0 bottom-0 h-20 bg-gradient-to-t from-black/40 to-transparent" />

        {/* Class / category pill */}
        {fleet_class?.name && (
          <span className="absolute left-4 top-4 inline-flex items-center rounded-full bg-text-color/85 px-3 py-1 text-[11px] font-semibold uppercase tracking-wider text-white backdrop-blur-sm">
            {fleet_class.name}
          </span>
        )}

        {/* Discount badge */}
        {hasDiscount && (
          <span className="absolute right-4 top-4 inline-flex items-center rounded-full bg-primary-200 px-3 py-1 text-[11px] font-bold uppercase tracking-wider text-on-primary shadow-lg shadow-primary-200/40">
            -{discountPercent}%
          </span>
        )}

        {/* Bonzah "available" badge — bottom-left over the gradient (parity
            with Design A). White pill so it stays legible on any theme. */}
        {shouldShowBonzahBadge && (
          <span className="absolute bottom-3 left-3 z-10 inline-flex items-center gap-1.5 rounded-full bg-white/90 px-2.5 py-1 text-[11px] font-semibold text-gray-800 shadow-sm backdrop-blur-sm">
            <img
              src={bonzahLogo}
              alt="Bonzah"
              className="h-4 w-4 object-contain"
              onError={(e: any) => {
                e.currentTarget.style.display = "none";
              }}
            />
            Bonzah available
          </span>
        )}
      </div>

      {/* Body */}
      <div className="flex flex-col p-5 sm:p-6 flex-1">
        {/* Title */}
        <h3 className="text-lg sm:text-xl font-bold text-text-color leading-tight truncate">
          {name}
        </h3>

        {/* Description */}
        <p
          className="mt-1 mb-4 text-sm text-secondary-text-color truncate"
          onMouseEnter={description ? handleMouseEnterDesc : undefined}
          onMouseLeave={description ? handleMouseLeaveDesc : undefined}
        >
          {description ? description.split(" ").slice(0, 7).join(" ") : " "}
        </p>

        {/* Spec strip */}
        <div className="grid grid-cols-3 gap-2 py-3 mb-5 rounded-2xl bg-secondary-50/60 border border-secondary-100">
          <Spec
            label={`${seats ?? "—"}`}
            sub="Seats"
            icon={
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <path d="M16 21v-2a4 4 0 0 0-4-4H6a4 4 0 0 0-4 4v2" />
                <circle cx="9" cy="7" r="4" />
                <path d="M22 21v-2a4 4 0 0 0-3-3.87" />
                <path d="M16 3.13a4 4 0 0 1 0 7.75" />
              </svg>
            }
          />
          <Spec
            label={`${doors ?? "—"}`}
            sub="Doors"
            divider
            icon={
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <path d="M14 22V4a2 2 0 0 0-2-2H6a2 2 0 0 0-2 2v18" />
                <path d="M2 22h16" />
                <path d="M11 11h.01" />
              </svg>
            }
          />
          <Spec
            label={transmission ? capitalize(String(transmission)) : color || "Auto"}
            sub={transmission ? "Trans." : "Color"}
            icon={
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <circle cx="12" cy="12" r="10" />
                <path d="M12 2a14.5 14.5 0 0 0 0 20" />
                <path d="M12 2a14.5 14.5 0 0 1 0 20" />
                <path d="M2 12h20" />
              </svg>
            }
          />
        </div>

        {/* Price + CTA */}
        <div className="mt-auto flex items-end justify-between gap-3">
          <div className="flex flex-col">
            {hasDiscount && (
              <span className="text-xs font-medium text-secondary-text-color line-through leading-none">
                ${formatPrice(increasedPriceNum)}
              </span>
            )}
            <span className="text-[11px] uppercase tracking-wider text-secondary-text-color font-medium mt-0.5">
              From
            </span>
            <span className="text-2xl font-extrabold text-text-color leading-none mt-0.5">
              ${formatPrice(currentPriceNum)}
              <span className="ml-1 text-xs font-medium text-secondary-text-color">
                /day
              </span>
            </span>
          </div>
          <button
            type="button"
            onClick={handleCtaClick}
            disabled={loadingId === id}
            className="group/cta inline-flex items-center gap-1.5 bg-button-color text-white px-4 sm:px-5 py-3 rounded-full text-sm font-semibold transition-all duration-300 hover:bg-primary-200 hover:text-on-primary hover:shadow-lg hover:shadow-primary-200/30 active:scale-95 disabled:opacity-60 disabled:cursor-wait"
          >
            {loadingId === id ? (
              <Spinner />
            ) : (
              <>
                Book
                <ArrowUpRightIcon className="h-4 w-4 transition-transform duration-300 group-hover/cta:translate-x-0.5 group-hover/cta:-translate-y-0.5" />
              </>
            )}
          </button>
        </div>
      </div>

      {/* Description tooltip */}
      {isHovered &&
        description &&
        createPortal(
          <div
            className="fixed bg-text-color text-white text-xs rounded-lg px-3 py-2 shadow-xl z-50 max-w-xs"
            style={{
              top: tooltipPosition.top + 24,
              left: tooltipPosition.left,
            }}
          >
            {description}
          </div>,
          document.body,
        )}
    </article>
  );
};

const Spec: FC<{
  label: string;
  sub: string;
  icon: React.ReactNode;
  divider?: boolean;
}> = ({ label, sub, icon, divider }) => (
  // 1NOW-1098: the parent grid pins each Spec to one-third of the card
  // width, so the label has a very narrow horizontal budget. The label
  // previously had `leading-none` and no wrap/truncate rule — long color
  // names like "Obsidian Black" or "Midnight Silver Metallic" overflowed
  // horizontally and crushed the SUB label against the bottom border.
  //
  // Fix: `text-center` + `break-words` + `leading-tight` let the label
  // wrap onto a second line cleanly when needed; `px-1` adds a 4px
  // horizontal cushion so wrapped text doesn't kiss the cell border;
  // `max-w-full` ensures the wrapping kicks in before overflow happens.
  // Short labels ("4", "Auto", "Red") still render on a single line.
  // The 3-column grid is row-equalising, so when one cell wraps to two
  // lines the other two grow with it — visual parity preserved.
  <div
    className={`flex flex-col items-center justify-center text-text-color min-w-0 ${
      divider ? "border-l border-r border-secondary-100" : ""
    }`}
  >
    <span className="text-text-color/80">{icon}</span>
    <span className="mt-1 text-sm font-semibold leading-tight text-center break-words max-w-full px-1">
      {label}
    </span>
    <span className="mt-0.5 text-[10px] uppercase tracking-wider text-secondary-text-color text-center">
      {sub}
    </span>
  </div>
);

const Spinner: FC = () => (
  <svg
    aria-hidden="true"
    className="h-5 w-5 animate-spin"
    viewBox="0 0 24 24"
    fill="none"
  >
    <circle cx="12" cy="12" r="10" stroke="currentColor" strokeOpacity="0.25" strokeWidth="4" />
    <path d="M12 2a10 10 0 0 1 10 10" stroke="currentColor" strokeWidth="4" strokeLinecap="round" />
  </svg>
);

const capitalize = (s: string) => (s ? s.charAt(0).toUpperCase() + s.slice(1) : "");

export default FleetCard;
